<template>
  <backbar-page-layout
    v-if="canPerform('authcore.users.get')"
    back-title="user_details_security.back_title"
    title="user_details_security.title"
    :user-id="id"
    :loading="isLoadingStatus"
  >
    <b-bsq-modal
      ref="modal"
      v-model="isModalEnabled"
      :header-title="modalHeaderTitle"
      :button-title="modalButtonTitle"
      :button-variant="modalButtonVariant"
      @ok="modalSubmit"
    >
      <div class="mt-3 mb-5">
        <div v-if="isChangePasswordModalEnabled">
          <i18n path="description.change_password" tag="div" class="mb-3">
            <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
          </i18n>
          <b-bsq-input
            v-model="newPassword"
            :disabled="actionLoading"
            :label="$t('input.password')"
            type="password"
            class="my-3" />
          <b-bsq-input
            v-model="confirmNewPassword"
            :disabled="actionLoading"
            :label="$t('input.confirm_password')"
            type="password"
            class="my-3" />
        </div>
      </div>
    </b-bsq-modal>
    <b-container class="mt-4">
      <h5 class="font-weight-lighter font:1.375rem">{{ $t('user_details_security.sub_title.change_password') }}</h5>
      <hr class="mt-1 mb-5" />
      <b-row class="ml-3 my-5">
        <b-col class="0.75rem">
          <div class="font-weight-bold">
            {{ $t('user_details_security.text.password') }}
          </div>
          <!-- TODO: Last changed requires server side support, see #346 -->
          <div v-if="false" class="shallow-transparency">
            {{ $t('user_details_security.text.last_changed', { date: '' }) }}
          </div>
        </b-col>
      </b-row>
      <b-row class="my-5">
        <b-col>
          <b-button
            class="px-4"
            variant="primary"
            @click="isChangePasswordModalEnabled = !isChangePasswordModalEnabled"
          >
            {{ $t('user_details_security.button.change_password') }}
          </b-button>
        </b-col>
      </b-row>

      <h5 class="font-weight-lighter font:1.375rem">{{ $t('user_details_security.sub_title.2_step_verification') }}</h5>
      <hr class="mt-1 mb-5" />
      <b-row class="ml-3">
        <b-col cols="4">
          <b-row align-v="center">
            <b-col class="font:0.75rem">
              <div class="font-weight-bold">
                {{ $t('user_details_security.text.2_step_verification') }}
              </div>
              <div class="shallow-transparency">
                <i18n v-if="firstlyCreatedFactor.createdAt" path="user_details_security.text.since" tag="div">
                  <span place="date">{{ firstlyCreatedFactor.createdAt }}</span>
                </i18n>
              </div>
              <div :class="{ 'text-success': isMFAEnabled, 'text-danger': !isMFAEnabled }">
                {{ $t(mfaStatus) }}
                <i v-if="isMFAEnabled" class="mx-1 text-success ac-icon ac-shield-tick"></i>
                <i v-else class="mx-1 text-danger ac-icon ac-minus-circle"></i>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-table
        class="mt-5 theme-general"
        thead-class="text-grey"
        :items="mfaDevices"
        :fields="fields"
      >
        <template v-slot:cell(type)="data">
          <span class="font-weight:semi-bold">{{ $t(`user_details_security.text.${data.value.toLowerCase()}`) }}</span>
        </template>
      </b-table>
    </b-container>
  </backbar-page-layout>
</template>

<script>
// Security page for user details.

// For change password action, using action in userDetails store which is the same as user details page to ensure the communciation between backend are the same

import { mapState, mapGetters } from 'vuex'

import BackbarPageLayout from '@/components/BackbarPageLayout.vue'

import store from '@/store'
import i18n from '@/i18n'

export default {
  name: 'UserDetailsSecurity',
  components: {
    BackbarPageLayout
  },

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      isChangePasswordModalEnabled: false,

      newPassword: '',
      confirmNewPassword: '',

      fields: [
        { key: 'type', label: i18n.t('user_details_security.table.header.mfa') },
        { key: 'value', label: i18n.t('user_details_security.table.header.description') },
        { key: 'lastUsedAt', label: i18n.t('user_details_security.table.header.last_used') }
      ]
    }
  },

  computed: {
    ...mapState('client', [
      'authenticated'
    ]),
    ...mapState('management/userDetails', [
      'user',

      'actionLoading',
      'actionError'
    ]),
    ...mapState('management/userDetails/security', [
      'loading',

      'mfaDevices'
    ]),
    ...mapGetters('management/userDetails/security', [
      'firstlyCreatedFactor'
    ]),
    isLoadingStatus () {
      return !this.authenticated || this.loading
    },
    isMFAEnabled () {
      return this.mfaDevices.length > 0
    },
    mfaStatus () {
      return this.isMFAEnabled ? 'general.on' : 'general.off'
    },
    isModalEnabled: {
      get () {
        return this.isChangePasswordModalEnabled
      },
      set () {
        setTimeout(() => {
          this.isChangePasswordModalEnabled = false
        }, 500)
      }
    },
    modalHeaderTitle () {
      if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonTitle () {
      if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonVariant () {
      if (this.isChangePasswordModalEnabled) {
        return 'primary'
      }
      return ''
    }
  },

  watch: {
    async authenticated (newVal) {
      if (newVal) {
        await store.dispatch('management/userDetails/security/list', this.id)
      } else {
        // Clear the error message from unauthenticated state
        store.commit('management/userDetails/security/CLEAR_STATES')
      }
    }
  },

  mounted () {
    store.dispatch('management/userDetails/security/list', this.id)
  },

  methods: {
    async modalSubmit () {
      if (this.isChangePasswordModalEnabled) {
        await this.changePassword()
      }

      if (this.actionError === undefined) {
        this.$nextTick(() => {
          this.isChangePasswordModalEnabled = false
        })
      }
    },
    async changePassword () {
      await store.dispatch('management/userDetails/changePassword', {
        newPassword: this.newPassword,
        confirmNewPassword: this.confirmNewPassword
      })
    }
  }
}
</script>

<style scoped lang="scss">
.ac-shield-tick,
.ac-minus-circle {
  font-size: 1.25rem;
  vertical-align: top;
  margin-top: -5px;
}
</style>
